/**
 * PreferencesForm Component
 * Step 3: Preferences form (optional)
 */

import { PoolTable3DViewer } from './PoolTable3DViewer.js';

export class PreferencesForm {
  constructor(containerId, initialData = {}, onDataChange, tableData = {}, roomData = {}) {
    this.container = document.getElementById(containerId);
    this.data = { ...initialData };
    this.tableData = tableData; // Table data for visualization
    this.roomData = roomData; // Room data for visualization
    this.onDataChange = onDataChange;
    this.viewer = null;
    this.viewerExpanded = true; // Track whether 3D preview is expanded (shown by default)
    this.render();
  }

  render() {
    // Destroy existing viewer before re-rendering to prevent memory leaks
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
    
    this.container.innerHTML = `
      <div class="form-container">
        <h2 class="form-title">Preferences</h2>
        <p class="form-description">Optional: Provide additional preferences to help us tailor recommendations.</p>
        
        <div class="form-grid">
          <div class="form-group full-width">
            <label for="additional-notes" class="form-label">
              Additional Notes
            </label>
            <textarea
              id="additional-notes"
              class="form-textarea"
              rows="4"
              placeholder="Any additional information or special requirements..."
            >${this.data.additionalNotes || ''}</textarea>
            <p class="form-hint">Optional notes about your installation needs</p>
          </div>
        </div>
        
        <div class="viewer-toggle-section" style="margin-top: 2rem;">
          <button class="viewer-toggle-btn" id="preferences-viewer-toggle" type="button">
            <span class="viewer-toggle-text">${this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview'}</span>
            <span class="viewer-toggle-icon">${this.viewerExpanded ? '▲' : '▼'}</span>
          </button>
          <div class="viewer-toggle-content" id="preferences-viewer-content" style="display: ${this.viewerExpanded ? 'block' : 'none'};">
            <div id="preferences-3d-viewer-container"></div>
          </div>
        </div>
      </div>
    `;

    // Add event listeners
    const inputs = this.container.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      input.addEventListener('input', () => this.handleInputChange());
      input.addEventListener('change', () => this.handleInputChange());
    });
    
    // Setup viewer toggle
    const viewerToggle = this.container.querySelector('#preferences-viewer-toggle');
    const viewerContent = this.container.querySelector('#preferences-viewer-content');
    if (viewerToggle && viewerContent) {
      viewerToggle.addEventListener('click', () => {
        this.viewerExpanded = !this.viewerExpanded;
        viewerContent.style.display = this.viewerExpanded ? 'block' : 'none';
        const toggleText = viewerToggle.querySelector('.viewer-toggle-text');
        const toggleIcon = viewerToggle.querySelector('.viewer-toggle-icon');
        if (toggleText) {
          toggleText.textContent = this.viewerExpanded ? 'Hide 3D Preview' : 'Show 3D Preview';
        }
        if (toggleIcon) {
          toggleIcon.textContent = this.viewerExpanded ? '▲' : '▼';
        }
        
        // Initialize viewer if it's being shown for the first time
        if (this.viewerExpanded && !this.viewer) {
          this.initViewer();
        }
      });
    }
    
    // Initialize viewer if it's already expanded (e.g., after re-render)
    if (this.viewerExpanded && !this.viewer) {
      this.initViewer();
    }
  }
  
  initViewer() {
    const viewerContainer = this.container.querySelector('#preferences-3d-viewer-container');
    if (!viewerContainer) {
      console.warn('Viewer container not found');
      return;
    }
    
    if (this.viewer) {
      // Viewer already exists, just update it
      this.updateViewer();
      return;
    }
    
    try {
      // Create new viewer
      this.viewer = new PoolTable3DViewer(viewerContainer, {
        width: 800,
        height: 400
      });
      // Update after a small delay to ensure Three.js is initialized
      setTimeout(() => {
        if (this.viewer) {
          this.updateViewer();
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing 3D viewer:', error);
      // Viewer will show its own error message
    }
  }
  
  updateViewer() {
    if (!this.viewer) {
      // If viewer doesn't exist, try to initialize it
      this.initViewer();
      return;
    }
    
    try {
      const params = {
        tableLength: this.tableData.length,
        tableWidth: this.tableData.width,
        tableHeightInches: this.tableData.heightInches,
        ceilingHeightInches: this.roomData.ceilingHeight || null,
        mountingOption: this.roomData.mountingOption || null,
      };
      
      this.viewer.updateParams(params);
    } catch (error) {
      console.error('Error updating viewer:', error);
    }
  }
  
  setTableData(tableData) {
    this.tableData = tableData;
    this.updateViewer();
  }
  
  setRoomData(roomData) {
    this.roomData = roomData;
    this.updateViewer();
  }

  handleInputChange() {
    this.data = {
      additionalNotes: this.container.querySelector('#additional-notes').value || null,
    };

    if (this.onDataChange) {
      this.onDataChange(this.data);
    }
  }

  getData() {
    return this.data;
  }

  setData(data) {
    this.data = { ...data };
    this.render();
  }
  
  destroy() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }
}

